import kozepesLogo from 'assets/icons/kozepes-telitettseg.svg'
import magasLogo from 'assets/icons/magas-telitettseg.svg'
import telthazLogo from 'assets/icons/majdnem-telthaz-telitettseg.svg'
import { ActionButton } from 'components/button/button.styled'
import ComplimentaryCheckbox from 'components/seatSelector/components/complimentaryCheckbox'
import { SubSectionContainer } from 'components/ticket/ticket.style'
import { useToken } from 'hooks/token'
import { useAutoTicketLockingForGeneralV2 } from 'hooks/user/useSelling/useAutoSeatLockingForGeneralV2'
import { useGeneralTickets } from 'hooks/user/useTickets/useGeneralTickets'
import { useReservationTimer } from 'hooks/user/useTickets/useReservationTimer'
import { useTicketsLockedByUser } from 'hooks/user/useTickets/useTicketsLockedByUser'
import { InputNumberValueChangeEvent } from 'primereact/inputnumber'
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo } from 'react'

import {
    ColoredLabel,
    Column,
    Container,
    Highlight,
    Label,
    PriceText,
    Row,
    StyledInputNumber,
    TelitettsegIcon,
} from './generalSelling.style'

interface GeneralAdmissionSellingProps {
    onSelectedSeatsChange: (selectedSeats: string[]) => void
    eventId: string
    actionLabel: string
    actionHandler: (selectedSeats: string[]) => void
    complimentaryButton?: boolean
    isComplimentary?: boolean
    setIsComplimentary?: Dispatch<SetStateAction<boolean>>
}

export const GeneralAdmissionSelling: React.FC<GeneralAdmissionSellingProps> = ({
    onSelectedSeatsChange,
    eventId,
    actionLabel,
    actionHandler,
    complimentaryButton = false,
    isComplimentary,
    setIsComplimentary,
}) => {
    const { sessionId } = useToken()
    const { data: generalTickets, isLoading, error, refetch } = useGeneralTickets(eventId)
    const { data: ticketsLockedByUser, refetch: ticketsLockedByUserRefetch } = useTicketsLockedByUser(eventId, sessionId ?? '')
    const { formatted: reservationTime } = useReservationTimer(ticketsLockedByUser)

    const memoizedOnSelectedSeatsChange = useCallback(onSelectedSeatsChange, [onSelectedSeatsChange])

    const onLocksDone = useCallback(() => {
        refetch()
        ticketsLockedByUserRefetch()
    }, [refetch, ticketsLockedByUserRefetch])

    const { amount, setAmount } = useAutoTicketLockingForGeneralV2({
        ticketsLockedByUser: ticketsLockedByUser ?? [],
        sessionId: sessionId ?? '',
        eventId,
        onSelectedTicketsChange: memoizedOnSelectedSeatsChange,
        onLocksDone,
    })

    const handleCountChange = (e: InputNumberValueChangeEvent) => {
        setAmount(e.value ?? 0)
    }

    useEffect(() => {
        const lockedByUser = ticketsLockedByUser?.length ?? 0
        const available = generalTickets?.availableCount ?? 0
        const maxSelectable = available + lockedByUser

        if (amount > maxSelectable) {
            setAmount(maxSelectable)
        }
    }, [generalTickets, ticketsLockedByUser, amount, setAmount])

    const soldStatus = useMemo(() => {
        if (!generalTickets?.allCount) {
            return { label: 'N/A', color: '#999', icon: null }
        }

        const available = generalTickets?.availableCount ?? 0
        const all = generalTickets.allCount

        if (available === 0) {
            return { label: 'Teltház', color: '#D32F2F', icon: telthazLogo }
        }

        const soldPercent = 100 - (available / all) * 100

        if (soldPercent < 50) {
            return { label: 'Közepes', color: '#00AC11', icon: kozepesLogo }
        }
        if (soldPercent < 75) {
            return { label: 'Magas', color: '#F4C000', icon: magasLogo }
        }

        return { label: 'Majdnem teltház', color: '#D32F2F', icon: telthazLogo }
    }, [generalTickets])

    return (
        <Container>
            <SubSectionContainer>
                <Row>
                    <Label>Nézőtér telítettsége:</Label>
                    <span style={{ color: soldStatus.color }}>
                        {soldStatus.icon && <TelitettsegIcon src={soldStatus.icon} alt={soldStatus.label} />}
                        <ColoredLabel>{soldStatus.label}</ColoredLabel>
                    </span>
                </Row>
            </SubSectionContainer>
            <SubSectionContainer>
                <Row>
                    <Column>
                        <Label>Megvásárolni kívánt jegyek:</Label>
                        <StyledInputNumber
                            value={amount}
                            onValueChange={handleCountChange}
                            showButtons
                            min={0}
                            max={generalTickets?.allCount ?? 0}
                            buttonLayout="horizontal"
                            decrementButtonClassName="p-button-secondary"
                            incrementButtonClassName="p-button-secondary"
                            disabled={isLoading || !!error || !generalTickets?.allCount}
                            suffix=" db"
                        />
                        {reservationTime && (
                            <Row>
                                <strong style={{ color: '#D32F2F' }}>{reservationTime}</strong>
                            </Row>
                        )}
                        {complimentaryButton && <ComplimentaryCheckbox isChecked={isComplimentary} onChange={setIsComplimentary} />}
                    </Column>
                </Row>
            </SubSectionContainer>
            <SubSectionContainer>
                <Row>
                    <PriceText>
                        <strong>Összesen:</strong> <Highlight>{isComplimentary ? 0 : (generalTickets?.price ?? 0) * amount} Ft</Highlight>
                    </PriceText>
                </Row>
            </SubSectionContainer>
            <Row>
                <ActionButton
                    label={actionLabel}
                    style={{ marginTop: '1rem', marginLeft: '1rem' }}
                    onClick={() => actionHandler(ticketsLockedByUser?.map((t) => t.seat_id) ?? [])}
                    disabled={amount === 0}
                />
            </Row>
        </Container>
    )
}
